import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  ChevronDown,
  Trash2,
  AlertCircle,
  Check,
  ArrowRight,
} from 'lucide-react';
import {
  educationRowHasError,
  getFirstEducationErrorRowIndex,
} from 'src/utils/resume-validation';
import { getEmptyEducation, educations as educationOptions } from './constants';
import { EducationFields, AddEducationButton } from './EducationFields';

const ErrDot = () => (
  <svg viewBox="0 0 8 8" aria-hidden>
    <circle cx="4" cy="4" r="3" fill="currentColor" opacity="0.5" />
  </svg>
);

export function EducationAccordionForm({
  control,
  watch,
  setValue,
  errors,
  resumeData,
  getEducationIndex,
  initialOpenIndex = null,
  validationActive = false,
  validationKey = 0,
}) {
  const [openIndex, setOpenIndex] = useState(initialOpenIndex);
  const [resolvedBannerFlash, setResolvedBannerFlash] = useState(false);
  const hasAutoExpandedRef = useRef(false);
  const previousErrorsRef = useRef(null);
  const isMountedRef = useRef(false);
  const isAddingEducationRef = useRef(false);
  const previousLengthRef = useRef(0);
  const entryCardRefs = useRef([]);
  const prevHadErrorsRef = useRef(false);
  const lastAutoScrollKeyRef = useRef(0);

  const openOnly = (index) => setOpenIndex(index);
  const closeAll = () => setOpenIndex(null);

  const getQualificationLabel = (qualificationValue) => {
    if (!qualificationValue) return 'New Entry';
    const qual = educationOptions.find((q) => q.value === qualificationValue);
    return qual?.label || qualificationValue;
  };
  const hasText = (val) => String(val || '').trim().length > 0;

  const getMissingLabelsForEducation = useCallback((edu = {}) => {
    const labels = [];
    if (!hasText(edu.qualification)) labels.push('Qualification');
    if (!hasText(edu.institution)) labels.push('Institution');
    if (!hasText(edu.graduationYear)) labels.push('Year');

    const isSchoolLevel =
      edu.qualification === 'TENTH' || edu.qualification === 'TWELFTH';
    if (isSchoolLevel && !hasText(edu.board)) labels.push('Board');
    if (
      edu.qualification &&
      edu.qualification !== 'TENTH' &&
      !hasText(edu.fieldsOfStudy)
    ) {
      labels.push('Field of Study');
    }
    if (
      (edu.qualification === 'DEGREE' || edu.qualification === 'MASTERS') &&
      !hasText(edu.university)
    ) {
      labels.push('University');
    }

    const gradeType = String(edu.gradeType || 'MARKS').toUpperCase();
    if (gradeType === 'GPA') {
      if (!hasText(edu.maxGPA)) labels.push('Max Score');
      if (!hasText(edu.earnedGPA)) labels.push('Score');
    } else {
      if (!hasText(edu.maxMarks)) labels.push('Max Score');
      if (!hasText(edu.earnedMarks)) labels.push('Score');
    }

    return labels;
  }, []);

  const mandatoryQualifications = [
    { type: 'TENTH', label: 'Secondary', index: getEducationIndex('TENTH') },
    {
      type: 'TWELFTH',
      label: 'Higher Secondary',
      index: getEducationIndex('TWELFTH'),
    },
    {
      type: 'DEGREE',
      label: "Bachelor's Degree",
      index: getEducationIndex('DEGREE'),
    },
  ];

  const allEducations = watch('educations') || [];
  const mandatoryIndices = new Set(
    mandatoryQualifications
      .map((q) => q.index)
      .filter((i) => typeof i === 'number' && i >= 0)
  );

  const allEducationEntries = allEducations.map((edu, index) => {
    const isMandatory = mandatoryIndices.has(index);
    const mandatoryQual = mandatoryQualifications.find(
      (q) => q.index === index
    );
    return {
      education: edu,
      index,
      isMandatory,
      mandatoryType: mandatoryQual?.type,
      mandatoryLabel: mandatoryQual?.label,
      title: isMandatory
        ? mandatoryQual?.label
        : getQualificationLabel(edu?.qualification),
    };
  });

  const errorEntryMeta = useMemo(
    () =>
      allEducationEntries
        .map(({ education, index, isMandatory, mandatoryType }) => {
          const finalEducation = isMandatory
            ? { ...education, qualification: mandatoryType }
            : education || getEmptyEducation();
          const missingLabels = getMissingLabelsForEducation(finalEducation);
          return {
            idx: index,
            missingLabels,
            missingCount: missingLabels.length,
          };
        })
        .filter((row) => row.missingCount > 0),
    [allEducationEntries, getMissingLabelsForEducation]
  );

  const errorEntryMap = useMemo(
    () => new Map(errorEntryMeta.map((row) => [row.idx, row])),
    [errorEntryMeta]
  );
  const hasAnyRowErrors = validationActive && errorEntryMeta.length > 0;
  const missingFieldTotal = errorEntryMeta.reduce(
    (sum, row) => sum + row.missingCount,
    0
  );
  const errorRowCount = errorEntryMeta.length;
  const bannerShow =
    validationActive && (hasAnyRowErrors || resolvedBannerFlash);

  useEffect(() => {
    if (!validationActive) {
      setResolvedBannerFlash(false);
      prevHadErrorsRef.current = false;
      return;
    }
    if (hasAnyRowErrors) {
      prevHadErrorsRef.current = true;
      setResolvedBannerFlash(false);
      return;
    }
    if (prevHadErrorsRef.current) {
      setResolvedBannerFlash(true);
      prevHadErrorsRef.current = false;
      const t = setTimeout(() => setResolvedBannerFlash(false), 2500);
      return () => clearTimeout(t);
    }
  }, [validationActive, hasAnyRowErrors]);

  useEffect(() => {
    const currentLength = allEducations.length;
    const previousLength = previousLengthRef.current;

    if (!isMountedRef.current) {
      isMountedRef.current = true;
      previousLengthRef.current = currentLength;
      return;
    }

    if (isAddingEducationRef.current && currentLength > previousLength) {
      isAddingEducationRef.current = false;
      previousLengthRef.current = currentLength;
      return;
    }

    if (currentLength < previousLength) {
      closeAll();
      hasAutoExpandedRef.current = false;
      previousErrorsRef.current = null;
    }

    previousLengthRef.current = currentLength;
  }, [allEducations.length]);

  useEffect(() => {
    if (!errors?.educations) {
      previousErrorsRef.current = null;
      return;
    }

    const errorsChanged =
      JSON.stringify(errors.educations) !==
      JSON.stringify(previousErrorsRef.current);

    if (errorsChanged && !hasAutoExpandedRef.current) {
      const firstErrorIndex = getFirstEducationErrorRowIndex(errors.educations);

      if (firstErrorIndex !== -1) {
        openOnly(firstErrorIndex);
        hasAutoExpandedRef.current = true;
      }
    }

    previousErrorsRef.current = errors.educations;
  }, [errors, allEducations.length]);

  useEffect(() => {
    if (!errors?.educations) {
      hasAutoExpandedRef.current = false;
      return;
    }
    const hasAnyErrors = errors.educations.some((e) => educationRowHasError(e));
    if (!hasAnyErrors) hasAutoExpandedRef.current = false;
  }, [errors, allEducations]);

  useEffect(() => {
    // Only auto-scroll on explicit validation attempts (keyed by Save),
    // not on every keystroke while the user is typing.
    if (!validationActive || !hasAnyRowErrors) return;
    if (!validationKey) return;
    if (validationKey === lastAutoScrollKeyRef.current) return;
    lastAutoScrollKeyRef.current = validationKey;
    const firstIdx = errorEntryMeta[0]?.idx ?? -1;
    if (firstIdx < 0) return;
    const t = setTimeout(() => {
      entryCardRefs.current[firstIdx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 150);
    return () => clearTimeout(t);
  }, [validationActive, validationKey, hasAnyRowErrors, errorEntryMeta]);

  const handleAddEducation = () => {
    const currentValues = watch();
    const currentLength = (currentValues.educations || []).length;
    const newIndex = currentLength;

    isAddingEducationRef.current = true;

    const updatedEducations = [
      ...(currentValues.educations || []),
      getEmptyEducation(),
    ];
    setValue('educations', updatedEducations);

    openOnly(newIndex);

    setTimeout(() => {
      setOpenIndex(newIndex);
    }, 0);
  };

  const handleRemoveEducation = (index) => {
    const currentValues = watch();
    const educations = currentValues.educations || [];
    setValue(
      'educations',
      educations.filter((_, i) => i !== index)
    );
    setOpenIndex((prev) => {
      if (prev === index) return null;
      if (prev !== null && prev > index) return prev - 1;
      return prev;
    });
  };

  const jumpToEntry = useCallback(
    (idx) => {
      openOnly(idx);
      setTimeout(() => {
        entryCardRefs.current[idx]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    },
    [openOnly]
  );

  return (
    <div className="edu-accordion-root">
      <div
        className={`edu-validation-banner ${bannerShow ? 'edu-validation-banner--show' : ''} ${
          !hasAnyRowErrors && resolvedBannerFlash
            ? 'edu-validation-banner--resolved'
            : ''
        }`}
      >
        <div className="edu-validation-banner-inner">
          <div className="edu-validation-banner-icon">
            <AlertCircle strokeWidth={2.5} />
          </div>
          <div className="edu-validation-banner-body">
            <div className="edu-validation-banner-title">
              {hasAnyRowErrors
                ? `${errorRowCount} ${errorRowCount === 1 ? 'entry needs' : 'entries need'} attention`
                : 'All entries are complete'}
            </div>
            <div className="edu-validation-banner-text">
              {hasAnyRowErrors
                ? `${missingFieldTotal} required ${
                    missingFieldTotal === 1 ? 'field is' : 'fields are'
                  } missing across your education entries.`
                : 'You can now save your changes.'}
            </div>
          </div>
        </div>
        {hasAnyRowErrors && errorEntryMeta.length > 0 && (
          <div className="edu-validation-banner-links">
            {errorEntryMeta.map(({ idx, missingCount }) => {
              const edu = allEducations[idx];
              const mand = mandatoryQualifications.find((q) => q.index === idx);
              const label =
                mand?.label ||
                getQualificationLabel(edu?.qualification) ||
                `Entry ${idx + 1}`;
              const fieldWord = missingCount === 1 ? 'field' : 'fields';
              return (
                <button
                  key={`edu-jump-${idx}`}
                  type="button"
                  className="edu-validation-banner-link"
                  onClick={() => jumpToEntry(idx)}
                >
                  <span className="edu-link-dot" />
                  {label} — {missingCount} {fieldWord}
                  <ArrowRight strokeWidth={2.5} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="edu-entry-list">
        {allEducationEntries.map(
          ({ education, index, isMandatory, mandatoryType, title }) => {
            const isOpen = openIndex === index;
            const finalEducation = isMandatory
              ? { ...education, qualification: mandatoryType }
              : education || getEmptyEducation();

            const rowMeta = errorEntryMap.get(index);
            const rowHasError = validationActive && Boolean(rowMeta);
            const inst = finalEducation?.institution?.trim?.() || '';
            const yr = finalEducation?.graduationYear?.trim?.() || '';
            const rowComplete =
              validationActive && !rowHasError && Boolean(inst && yr);

            const tagLabels = rowHasError ? rowMeta.missingLabels : [];

            const cardClass = [
              'edu-entry-card',
              isOpen && 'edu-entry-card--active',
              rowHasError && 'edu-entry-card--error',
              rowComplete && 'edu-entry-card--complete',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div
                key={`education-${index}`}
                id={`edu-entry-card-${index}`}
                ref={(el) => {
                  entryCardRefs.current[index] = el;
                }}
                className={`group ${cardClass}`}
              >
                <div
                  className="edu-entry-head"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenIndex((prev) => (prev === index ? null : index));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpenIndex((prev) => (prev === index ? null : index));
                    }
                  }}
                >
                  <div className="edu-entry-indicator">
                    <span className="edu-entry-indicator-num">{index + 1}</span>
                    <span className="edu-entry-indicator-check" aria-hidden>
                      <Check strokeWidth={2.5} />
                    </span>
                  </div>
                  <div className="edu-entry-summary">
                    <div className="edu-entry-summary-title">{title}</div>
                    <div className="edu-entry-summary-sub">
                      {finalEducation?.institution
                        ? `${finalEducation.institution}${
                            finalEducation?.graduationYear
                              ? ` · ${finalEducation.graduationYear}`
                              : ''
                          }`
                        : 'Click to add details'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!isMandatory && (
                      <button
                        type="button"
                        className="btn-icon-sm w-7 h-7 rounded-[6px] border-none bg-transparent flex items-center justify-center cursor-pointer text-gray-400 transition-all opacity-0 group-hover:opacity-100 hover:bg-ah-status-error-hover-bg hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveEducation(index);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <div className="edu-entry-toggle">
                      <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                {rowHasError && tagLabels.length > 0 && (
                  <div className="edu-entry-error-detail">
                    {tagLabels.map((label) => (
                      <span
                        key={`${index}-${label}`}
                        className="edu-error-field-tag"
                      >
                        <ErrDot /> {label}
                      </span>
                    ))}
                  </div>
                )}

                <div className="edu-entry-form">
                  <div className="edu-entry-form-inner">
                    <div className="edu-entry-form-divider" />
                    <EducationFields
                      education={finalEducation}
                      index={index}
                      control={control}
                      watch={watch}
                      setValue={setValue}
                      errors={errors}
                      masterData={resumeData?.masterData || {}}
                      onRemove={null}
                      isLockedQualification={isMandatory}
                      lockedQualificationType={mandatoryType}
                      validationActive={validationActive}
                      validationKey={validationKey}
                    />
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>

      <AddEducationButton onClick={handleAddEducation} />
    </div>
  );
}
